import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {doNotTranslateYet} from "@khan/i18n";
import {colorUtil, mediaQueries} from "@khan/shared-styles";

import PageDisplay from "#data-access/page-display.tsx";

import SimplifiedPageHeader from "../../components/simplified-page-header.tsx";

import JoinPageDecorations from "./join-page-decorations.tsx";

type Props = {
    detailScreenNode: React.ReactNode;
    actionScreenNode: React.ReactNode;
    onBackClick?: () => void;
    backHref?: string;
};

/**
 * JoinPageLayout is the page layout for the Join page! This includes a pretty
 * full-screen background, and the two-column layout.
 *
 * We use this both in JoinWidget, the entry point for the Join experience; and
 * in DistrictsJoinWidget, the additional Join pages for the District account
 * activation experience.
 */
class JoinPageLayout extends React.Component<Props> {
    render(): React.ReactNode {
        const {detailScreenNode, actionScreenNode, onBackClick, backHref} =
            this.props;

        return (
            <PageDisplay
                settings={{
                    title: t`Join`,
                    header: false,
                    footer: false,
                }}
            >
                <View style={styles.container}>
                    <View style={styles.decorationsWrapper}>
                        <JoinPageDecorations />
                    </View>
                    <View style={styles.content}>
                        <SimplifiedPageHeader backgroundColor="darkBlue" />
                        <View style={styles.screens}>
                            <View tag="section" style={styles.screen}>
                                <View style={styles.detailScreenContent}>
                                    {detailScreenNode}
                                </View>
                            </View>
                            <View
                                tag="section"
                                style={[styles.screen, styles.actionScreen]}
                            >
                                {actionScreenNode}
                                {(onBackClick || backHref) && (
                                    <View style={styles.backLink}>
                                        <Button
                                            kind="tertiary"
                                            onClick={onBackClick}
                                            href={backHref}
                                        >
                                            {doNotTranslateYet`Back`}
                                        </Button>
                                    </View>
                                )}
                            </View>
                        </View>
                    </View>
                </View>
            </PageDisplay>
        );
    }
}

const darkBlue75 = colorUtil.format(
    colorUtil.fade(colorUtil.parse(color.darkBlue), 0.75),
);

// HACK(mdr): The design calls for 96px margins... this seems like the best
//     approximation!
const xxxxLarge = spacing.xLarge_32 + spacing.xxxLarge_64;

const styles = StyleSheet.create({
    backLink: {
        marginLeft: 32,
        marginRight: "auto",
        marginTop: "auto",
        marginBottom: 20,
        color: color.blue,
        fontSize: 15,
        lineHeight: "40px",
        fontWeight: 600,
        display: "flex",
        alignItems: "center",
        backgroundColor: color.white,
        bottom: 0,
        left: 0,
    },
    container: {
        position: "relative",
        backgroundColor: color.darkBlue,
    },

    decorationsWrapper: {
        position: "absolute",
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,

        [mediaQueries.smOrSmaller]: {
            display: "none",
        },
    },

    content: {
        alignItems: "center",

        [mediaQueries.lgOrLarger]: {
            minHeight: "100vh",
        },
    },

    screens: {
        flex: "1 0 auto",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",

        [mediaQueries.lgOrLarger]: {
            flexDirection: "row",
            paddingTop: spacing.xxLarge_48,
            paddingBottom: spacing.xxxLarge_64,
        },

        [mediaQueries.md]: {
            // Leave space at the bottom for the decorations!
            paddingBottom: 200,
        },

        [mediaQueries.mdOrSmaller]: {
            flexDirection: "column",
        },
    },

    screen: {
        [mediaQueries.lgOrLarger]: {
            flex: "0 1 auto",
            width: 530,
            minHeight: 475,
            maxHeight: "80%",
        },

        [mediaQueries.md]: {
            width: "70%",
        },

        [mediaQueries.smOrSmaller]: {
            width: "100%",
        },
    },

    // This wraps the actual content of the `detailScreen`, and applies a
    // background. That way, if it overlaps part of the background decoration,
    // the text is still entirely legible!
    detailScreenContent: {
        backgroundColor: darkBlue75,
        borderRadius: 4,

        padding: spacing.medium_16,

        [mediaQueries.lgOrLarger]: {
            // We want this to be xxxxLarge away from things visually, but have
            // medium padding for its usually-invisible background, just in
            // case.
            margin: xxxxLarge - spacing.medium_16,
        },

        [mediaQueries.mdOrSmaller]: {
            // We want this to be xxxLarge away from things visually, but have
            // medium padding for its usually-invisible background, just in
            // case.
            margin: spacing.xxxLarge_64 - spacing.medium_16,
        },

        [mediaQueries.smOrSmaller]: {
            margin: 0,
            padding: spacing.large_24,
        },
    },

    actionScreen: {
        backgroundColor: color.white,
        borderRadius: 4,

        // HACK(mdr): This is what the JoinWidget content expects, even though
        //     it's kinda unintuitive. There might be a better way to resolve
        //     this!
        flexDirection: "column",

        [mediaQueries.mdOrSmaller]: {
            minHeight: 475,
        },

        [mediaQueries.smOrSmaller]: {
            borderRadius: 0,
        },
    },
});

export default JoinPageLayout;
